import React, { useState, useEffect, useContext } from 'react';
import { complaintAPI } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
import { formatDwellingUnit } from '../../utils/brandingUtils';
import { AuthContext } from '../../contexts/AuthContext';
import PageTitle from '../shared/PageTitle';
import { getCloseIconUrl } from '../../utils/closeIconUrl';

// Define categories not relevant for maintenance users
const NON_MAINTENANCE_CATEGORIES = ['security', 'billing', 'amenities', 'noise', 'general'];

// Reuse the StatusButtonGroup component from admin complaints
const StatusButtonGroup = ({ status, complaintId, onStatusChange }) => {
  return (
    <div className="btn-group btn-group-sm" role="group">
      <button
        type="button"
        className={`btn ${status === 'open' ? 'btn-danger' : 'btn-outline-danger'}`}
        onClick={() => onStatusChange(complaintId, 'open')}
        disabled={status === 'open'}
        title="Open"
      >
        <i className="fas fa-exclamation-circle me-1"></i> Open
      </button>
      <button
        type="button"
        className={`btn ${status === 'in_progress' ? 'btn-warning' : 'btn-outline-warning'}`}
        onClick={() => onStatusChange(complaintId, 'in_progress')}
        disabled={status === 'in_progress'}
        title="In Progress"
      >
        <i className="fas fa-tools me-1"></i> In Progress
      </button>
      <button
        type="button"
        className={`btn ${status === 'resolved' ? 'btn-success' : 'btn-outline-success'}`}
        onClick={() => onStatusChange(complaintId, 'resolved')}
        disabled={status === 'resolved'}
        title="Resolved"
      >
        <i className="fas fa-check me-1"></i> Resolved
      </button>
    </div>
  );
};

const Complaints = () => {
  const { currentUser } = useContext(AuthContext);
  // Dynamic list of categories for dropdown
  const [availableCategories, setAvailableCategories] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { settings } = useSettings();
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  // Get dwelling unit name
  const unitSingular = formatDwellingUnit(settings);

  // For complaint details/response
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [responseLoading, setResponseLoading] = useState(false);

  // For filtering
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch complaints
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const complaintsRes = await complaintAPI.getAllComplaints({
          status: filterStatus === 'all' ? '' : filterStatus,
          // Always fetch server-side filter by selected category only
          category: filterCategory === 'all' ? '' : filterCategory
        });

        // Initialize responses array if not present
        const complaintsData = complaintsRes.data.map(complaint => ({
          ...complaint,
          responses: complaint.responses || []
        }));

        // For maintenance users, remove non-maintenance categories
        let processedData = complaintsData;
        if (currentUser?.role === 'maintenance') {
          processedData = processedData.filter(c => !NON_MAINTENANCE_CATEGORIES.includes(c.category));
        }
        setComplaints(processedData);
        setFilteredComplaints(processedData);
        // Derive available categories for dropdown
        const cats = Array.from(new Set(processedData.map(c => c.category)));
        setAvailableCategories(['all', ...cats]);
        setError(null);
      } catch (err) {
        console.error('Error fetching complaints data:', err);
        setError('Failed to load complaints. Please check the API connection and try again.');
        setComplaints([]);
        setFilteredComplaints([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filterStatus, filterCategory, currentUser?.role]);

  // Apply filters when filterStatus, filterCategory, or searchTerm changes
  useEffect(() => {
    let results = [...complaints];

    // Apply status filter
    if (filterStatus !== 'all') {
      results = results.filter(complaint => complaint.status === filterStatus);
    }

    // Apply category filter
    if (filterCategory !== 'all') {
      results = results.filter(complaint => complaint.category === filterCategory);
    }

    // Apply search filter
    if (searchTerm.trim() !== '') {
      const search = searchTerm.toLowerCase();
      results = results.filter(complaint =>
        (complaint.subject && complaint.subject.toLowerCase().includes(search)) ||
        (complaint.title && complaint.title.toLowerCase().includes(search)) ||
        (complaint.description && complaint.description.toLowerCase().includes(search)) ||
        (complaint.villa_number && complaint.villa_number.toLowerCase().includes(search))
      );
    }

    setFilteredComplaints(results);
  }, [complaints, filterStatus, filterCategory, searchTerm]);

  const handleOpenModal = (complaint) => {
    setSelectedComplaint(complaint);
    setResponseText('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedComplaint(null);
    setResponseText('');
  };

  const handleAddResponse = async () => {
    if (!responseText.trim()) return;

    try {
      setResponseLoading(true);

      const response = await complaintAPI.addResponse(selectedComplaint.id, { text: responseText });

      // Update the complaints list with the new response
      const updatedComplaints = complaints.map(complaint => {
        if (complaint.id === selectedComplaint.id) {
          const updatedResponses = [...(complaint.responses || []), response.data.response];
          return { ...complaint, responses: updatedResponses };
        }
        return complaint;
      });

      setComplaints(updatedComplaints);

      // Reset form
      setResponseText('');

      // Close modal
      handleCloseModal();
    } catch (err) {
      console.error('Error adding response:', err);
      setError('Failed to add response. Please try again.');
    } finally {
      setResponseLoading(false);
    }
  };

  const handleStatusChange = async (complaintId, newStatus) => {
    try {
      await complaintAPI.updateStatus(complaintId, newStatus);

      // Update the complaints list with the new status
      const updatedComplaints = complaints.map(complaint => {
        if (complaint.id === complaintId) {
          return { ...complaint, status: newStatus };
        }
        return complaint;
      });

      setComplaints(updatedComplaints);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open':
        return 'bg-danger';
      case 'in_progress':
        return 'bg-warning';
      case 'resolved':
        return 'bg-success';
      default:
        return 'bg-secondary';
    }
  };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'maintenance':
        return <span className="badge bg-info">Maintenance</span>;
      case 'security':
        return <span className="badge bg-danger">Security</span>;
      case 'amenities':
        return <span className="badge bg-success">Amenities</span>;
      case 'billing':
        return <span className="badge bg-warning">Billing</span>;
      case 'noise':
        return <span className="badge bg-dark">Noise</span>;
      default:
        return <span className="badge bg-secondary">General</span>;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <PageTitle icon="fa-tools" title="Complaints / Suggestions Management" />

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="statusFilter" className="form-label">Status</label>
              <select
                id="statusFilter"
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <div className="col-md-4">
              <label htmlFor="categoryFilter" className="form-label">Category</label>
              <select
                id="categoryFilter"
                className="form-select"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                {availableCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label htmlFor="searchInput" className="form-label">Search</label>
              <input
                id="searchInput"
                type="text"
                className="form-control"
                placeholder="Search complaints / suggestions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Complaints List */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Complaints / Suggestions</h5>
          <span className="badge bg-primary">{filteredComplaints.length}</span>
        </div>
        <div className="card-body">
          {filteredComplaints.length === 0 ? (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              No complaints found matching your filters.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>{unitSingular}</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredComplaints.map(complaint => (
                    <tr key={complaint.id}>
                      <td>#{complaint.id}</td>
                      <td>{complaint.title || complaint.subject}</td>
                      <td>{complaint.villa_number}</td>
                      <td>{getCategoryBadge(complaint.category)}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(complaint.status)}`}>
                          {complaint.status === 'in_progress' ? 'In Progress' : complaint.status.charAt(0).toUpperCase() + complaint.status.slice(1)}
                        </span>
                      </td>
                      <td>{formatDate(complaint.created_at)}</td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => handleOpenModal(complaint)}
                            title="View Details & Respond"
                          >
                            <i className="fas fa-comment"></i>
                          </button>
                          <StatusButtonGroup
                            status={complaint.status}
                            complaintId={complaint.id}
                            onStatusChange={handleStatusChange}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Response Modal */}
      {showModal && selectedComplaint && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
                <h5 className="modal-title">
                  Complaint #{selectedComplaint.id} - {selectedComplaint.title || selectedComplaint.subject}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <p><strong>Unit:</strong> {selectedComplaint.villa_number}</p>
                    <p><strong>Category:</strong> {selectedComplaint.category}</p>
                    <p>
                      <strong>Status:</strong>
                      <span className={`badge ms-2 ${getStatusBadgeClass(selectedComplaint.status)}`}>
                        {selectedComplaint.status === 'in_progress' ? 'In Progress' : selectedComplaint.status.charAt(0).toUpperCase() + selectedComplaint.status.slice(1)}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Date:</strong> {formatDate(selectedComplaint.created_at)}</p>
                    <p><strong>Last Updated:</strong> {formatDate(selectedComplaint.updated_at || selectedComplaint.created_at)}</p>
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">Description</h6>
                  </div>
                  <div className="card-body">
                    <p>{selectedComplaint.description}</p>
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">Responses</h6>
                  </div>
                  <div className="card-body">
                    {selectedComplaint.responses && selectedComplaint.responses.length > 0 ? (
                      <div className="response-timeline">
                        {selectedComplaint.responses.map((response, index) => (
                          <div key={index} className="response-item mb-3 p-3 border-start border-4 border-primary">
                            <div className="d-flex justify-content-between mb-2">
                              <strong>{response.created_by || 'Staff'}</strong>
                              <small>{formatDate(response.created_at)}</small>
                            </div>
                            <p className="mb-0">{response.text}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="alert alert-info">No responses yet.</div>
                    )}
                  </div>
                </div>

                <div className="card">
                  <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">Add Response</h6>
                  </div>
                  <div className="card-body">
                    <textarea
                      className="form-control mb-3"
                      rows="3"
                      placeholder="Enter your response..."
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <div className="d-flex flex-wrap gap-3 w-100 justify-content-between">
                  <div className='w-100'>
                    <StatusButtonGroup
                      status={selectedComplaint.status}
                      complaintId={selectedComplaint.id}
                      onStatusChange={(id, status) => {
                        handleStatusChange(id, status);
                        setSelectedComplaint({ ...selectedComplaint, status });
                      }}
                    />
                  </div>
                  <div className='w-100'>
                    <button type="button" className="btn btn-secondary me-2" onClick={handleCloseModal}>
                      Close
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddResponse}
                      disabled={!responseText.trim() || responseLoading}
                    >
                      {responseLoading ? 'Sending...' : 'Send Response'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Complaints; 